import React from 'react'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Avatar from '@material-ui/core/Avatar'
import Card from '@material-ui/core/Card'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import CalendarTodayIcon from 'components/material-symbol/icons/CalendarToday'
import FavoriteIcon from 'components/material-symbol/icons/Favorite'
import LocationOnIcon from 'components/material-symbol/icons/LocationOn'
import PGAIcon from 'images/pga-seal-icon.svg'
import { centsToDollarsFormatted } from 'lib/utils/number'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  heading: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    boxShadow: 'none',
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white,
  },
  coachHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
  },
  avatar: {
    width: 42,
    height: 42,
    flexShrink: 0,
    backgroundColor: '#DCE6FF',
    color: '#1C4D94',
    fontSize: '0.875rem',
    fontWeight: 700,
  },
  coachIdentity: {
    minWidth: 0,
    flex: 1,
  },
  coachNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  coachName: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  pgaBadge: {
    width: 18,
    height: 18,
    flexShrink: 0,
  },
  coachTitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  priceAction: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  price: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  favoriteIcon: {
    width: 24,
    height: 24,
    color: '#70787C',
  },
  listingBody: {
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0, 1.5, 1.5),
  },
  image: {
    position: 'relative',
    width: 116,
    height: 106,
    borderRadius: 8,
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    overflow: 'hidden',
  },
  imagePlaceholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
    fontSize: '0.6875rem',
    textAlign: 'center',
    padding: theme.spacing(1),
  },
  listingDetails: {
    minWidth: 0,
    flex: 1,
  },
  title: {
    fontFamily: 'Source Serif Pro, Georgia, serif',
    fontSize: '1.125rem',
    fontWeight: 700,
    lineHeight: 1.25,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.75),
    overflowWrap: 'anywhere',
  },
  description: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    color: theme.palette.text.secondary,
    fontSize: '0.6875rem',
    lineHeight: 1.35,
    marginBottom: theme.spacing(0.75),
  },
  detail: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.6875rem',
    lineHeight: 1.4,
    '& + &': {
      marginTop: theme.spacing(0.5),
    },
  },
  detailIcon: {
    width: 15,
    height: 15,
    fontSize: 15,
    color: theme.palette.text.secondary,
    marginTop: 1,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.25, 1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  tag: {
    height: 22,
    borderRadius: 11,
    fontSize: '0.625rem',
    color: '#7A889B',
    backgroundColor: '#F3F5F6',
  },
  registerButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 78,
    height: 34,
    padding: theme.spacing(0, 1.5),
    borderRadius: 18,
    color: theme.palette.common.white,
    backgroundColor: '#3978B8',
    fontSize: '0.8125rem',
    fontWeight: 600,
    flexShrink: 0,
  },
}))

const toDateTime = value => {
  if (DateTime.isDateTime(value)) return value
  if (value instanceof Date) return DateTime.fromJSDate(value)
  return null
}

export const formatPreviewSession = session => {
  const date = toDateTime(session?.startDate)
  const time = toDateTime(session?.startTime)

  if (!date?.isValid) return null

  const dateLabel = date.toFormat('EEEE, LLL d')
  return time?.isValid
    ? `${dateLabel} at ${time.toFormat('h:mm a')}`
    : dateLabel
}

const getPreviewDescription = value => {
  if (!value) return ''

  try {
    const { blocks = [] } = JSON.parse(value)
    return blocks
      .map(block => block.text)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim()
  } catch {
    return String(value)
  }
}

const getBackgroundImage = url =>
  `url("${url.replace(/["\\\n]/g, encodeURIComponent)}")`

const ListingPreview = ({ coachFacilities, coachName }) => {
  const classes = useStyles()
  const { watch } = useFormContext()
  const title = watch('title')
  const tags = watch('tags') || []
  const locationId = watch('locationId')
  const sessions = watch('sessions') || []
  const price = watch('price')
  const description = watch('description')
  const featuredImageUrl = watch('featuredImageUrl')

  const selectedFacility = (coachFacilities || []).find(
    facility => facility.id === locationId,
  )
  const sessionLabel = formatPreviewSession(sessions[0])
  const parsedPrice = Number.parseFloat(String(price).replace(/,/g, ''))
  const hasPrice = Number.isFinite(parsedPrice)
  const priceLabel = hasPrice
    ? centsToDollarsFormatted(parsedPrice * 100)
    : 'Price not set'
  const previewDescription = getPreviewDescription(description)
  const displayName = coachName || 'Your PGA profile'
  const coachInitials = displayName
    .split(' ')
    .map(namePart => namePart[0])
    .join('')
    .slice(0, 2)

  return (
    <Box
      className={classes.root}
      component="aside"
      aria-label="Listing preview"
    >
      <Typography className={classes.heading}>Preview</Typography>
      <Card className={classes.card}>
        <Box className={classes.coachHeader}>
          <Avatar className={classes.avatar} alt={displayName}>
            {coachInitials}
          </Avatar>
          <Box className={classes.coachIdentity}>
            <Box className={classes.coachNameRow}>
              <Typography className={classes.coachName}>
                {displayName}
              </Typography>
              <img className={classes.pgaBadge} src={PGAIcon} alt="PGA" />
            </Box>
            <Typography className={classes.coachTitle}>
              PGA Teaching Professional
            </Typography>
          </Box>
          <Box className={classes.priceAction}>
            <Typography className={classes.price}>{priceLabel}</Typography>
            <FavoriteIcon
              className={classes.favoriteIcon}
              aria-label="Favorite preview"
            />
          </Box>
        </Box>
        <Box className={classes.listingBody}>
          <Box
            className={classes.image}
            data-testid="listing-preview-image"
            style={
              featuredImageUrl
                ? { backgroundImage: getBackgroundImage(featuredImageUrl) }
                : undefined
            }
            role="img"
            aria-label={title ? `${title} event image` : 'Event image preview'}
          >
            {!featuredImageUrl ? (
              <Typography className={classes.imagePlaceholder}>
                Event image
              </Typography>
            ) : null}
          </Box>
          <Box className={classes.listingDetails}>
            <Typography className={classes.title}>
              {title || 'Your event name'}
            </Typography>
            <Typography className={classes.description}>
              {previewDescription ||
                'Add a description so students know what to expect.'}
            </Typography>
            {selectedFacility ? (
              <Box className={classes.detail}>
                <LocationOnIcon className={classes.detailIcon} />
                <span>{selectedFacility.name}</span>
              </Box>
            ) : null}
            {sessionLabel ? (
              <Box className={classes.detail}>
                <CalendarTodayIcon className={classes.detailIcon} />
                <span>{sessionLabel}</span>
              </Box>
            ) : null}
          </Box>
        </Box>
        <Box className={classes.footer}>
          <Box className={classes.tags}>
            {tags.slice(0, 4).map(tag => (
              <Chip key={tag} className={classes.tag} label={tag} />
            ))}
          </Box>
          <Box className={classes.registerButton}>Register</Box>
        </Box>
      </Card>
    </Box>
  )
}

ListingPreview.propTypes = {
  coachFacilities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  coachName: PropTypes.string,
}

export default ListingPreview
